import React from 'react';
import { Sparkles, ArrowRight, Settings2, FileText, Brain, Globe, Target } from 'lucide-react';
import { Button } from '../common/Button';
import { useNavigate } from 'react-router-dom';

// Desktop Horizontal Progress Flow Component
const HorizontalProgressFlow = () => {
  const steps = [
    { icon: <FileText className="w-4 h-4" />, text: "Analyze Resume", animationClass: "animate-step-activate-blue" },
    { icon: <Brain className="w-4 h-4" />, text: "AI Processing", animationClass: "animate-step-activate-purple" },
    { icon: <Globe className="w-4 h-4" />, text: "Curate News", animationClass: "animate-step-activate-green" },
    { icon: <Target className="w-4 h-4" />, text: "Deliver Results", animationClass: "animate-step-activate-orange" }
  ];

  return (
    <div className="flex items-center justify-between w-full gap-1">
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          <div className="flex flex-col items-center text-center relative z-10 flex-1">
            <div 
              className={`p-1.5 rounded-full mb-1 bg-gray-400 dark:bg-gray-600 ${step.animationClass}`}
              style={{
                animationDelay: `${index * 1.5}s`,
                animationFillMode: 'both'
              }}
            >
              <div className="text-white">
                {step.icon}
              </div>
            </div>
            <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark font-medium leading-tight">
              {step.text}
            </span>
          </div>
          
          {/* Horizontal line after each circle except the last */}
          {index < 3 && (
            <div className="flex-1 relative flex items-center" style={{ marginTop: '-8px' }}>
              <div className="w-full h-0.5 bg-gray-300 dark:bg-gray-600"></div>
              <div 
                className="absolute top-0 left-0 h-0.5 bg-primary-light dark:bg-primary-dark w-0 animate-line-progress"
                style={{
                  animationDelay: `${index * 1.5 + 0.5}s`,
                  animationFillMode: 'both'
                }}
              ></div>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Mobile Vertical Progress Flow Component
const VerticalProgressFlow = () => {
  const steps = [
    { icon: <FileText className="w-4 h-4" />, text: "Analyze Resume", animationClass: "animate-step-activate-blue" },
    { icon: <Brain className="w-4 h-4" />, text: "AI Processing", animationClass: "animate-step-activate-purple" },
    { icon: <Globe className="w-4 h-4" />, text: "Curate News", animationClass: "animate-step-activate-green" },
    { icon: <Target className="w-4 h-4" />, text: "Deliver Results", animationClass: "animate-step-activate-orange" }
  ];

  return (
    <div className="flex flex-col items-center gap-2">
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          <div className="flex flex-col items-center text-center relative z-10">
            <div 
              className={`p-2 rounded-full mb-2 bg-gray-400 dark:bg-gray-600 ${step.animationClass}`}
              style={{
                animationDelay: `${index * 1.5}s`,
                animationFillMode: 'both'
              }}
            >
              <div className="text-white">
                {step.icon}
              </div>
            </div>
            <span className="text-sm text-text-secondary-light dark:text-text-secondary-dark font-medium">
              {step.text}
            </span>
          </div>
          
          {/* Vertical line after each circle except the last */}
          {index < 3 && (
            <div className="relative flex flex-col items-center" style={{ marginTop: '8px', marginBottom: '8px' }}>
              <div className="w-0.5 h-8 bg-gray-300 dark:bg-gray-600"></div>
              <div 
                className="absolute top-0 left-0 w-0.5 bg-primary-light dark:bg-primary-dark h-0 animate-line-progress-vertical"
                style={{
                  animationDelay: `${index * 1.5 + 0.5}s`,
                  animationFillMode: 'both'
                }}
              ></div>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

interface SkillNewsCardProps {
  isMobileView?: boolean;
}

export const SkillNewsCard: React.FC<SkillNewsCardProps> = ({ isMobileView = false }) => {
  const navigate = useNavigate();

  return (
    <div className="p-4 md:p-6 border rounded-xl border-border-light dark:border-border-dark bg-gradient-to-br from-background-light/50 to-primary-light/5 dark:from-background-dark/50 dark:to-primary-dark/5 relative overflow-hidden">

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-6 left-6 w-16 h-16 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-full blur-lg animate-bounce"></div>
        <div className="absolute top-1/2 left-1/3 w-12 h-12 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-md animate-float"></div>
      </div>

      <div className="relative z-10">
        <h2 className="mb-4 text-lg md:text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
          Skill-based News
        </h2>
        {/* How it works animation */}
        <div className="mb-6 p-4 bg-primary-light/10 dark:bg-primary-dark/10 rounded-lg border border-primary-light/20 dark:border-primary-dark/20">
          <h4 className="font-semibold text-text-primary-light dark:text-text-primary-dark text-sm mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary-light dark:text-primary-dark" />
            <span className="text-base text-text-primary-light dark:text-text-primary-dark font-heading font-semibold">How it works</span>
          </h4>
          <div className="mt-4 md:mt-0">
            {isMobileView ? <VerticalProgressFlow /> : <HorizontalProgressFlow />}
          </div>
        </div>

        {/* Call to action */}
        <div className="text-center">
          <div className="mb-4">
            {/* <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-light/10 dark:bg-primary-dark/10 rounded-full text-xs font-medium border border-primary-light/20 dark:border-primary-dark/20">
              <div className="w-2 h-2 bg-primary-light dark:bg-primary-dark rounded-full animate-pulse"></div>
              <span className="text-primary-light dark:text-primary-dark">Ready to explore</span>
            </div> */}
          </div>
          <h4 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
            Your personalized news feed awaits!
          </h4>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-6">
            Get the latest industry insights, trending topics, and skill-specific updates curated just for you.
          </p>
          <div className="flex flex-col gap-3 justify-center">
            <Button
              onClick={() => navigate('/feed')}
              variant="primary"
              size="md"
              className="text-sm py-3.5"
              rightIcon={<ArrowRight className="w-4 h-4" />}
            >
              <span className="relative">Explore Your News Feed</span>
            </Button>
            <Button
              onClick={() => navigate('/settings-preferences?tab=preferences')}
              variant="secondary"
              size="md"
              leftIcon={<Settings2 size={16} />}
              className="text-sm py-3"
            >
              Update Preferences
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}; 